import { useEffect, useState } from 'react';
import type { PingConnection } from '../api';
import {
  Banner, Card, CheckBox, Code, Copyable, ListField, SelectField, TextField,
} from '../components/ui';
import { ConnectionPicker, NoConnections, useConnections } from '../components/useConnections';

export function PingTab({ tenantId }: { tenantId: string }) {
  const base = `/api/tenants/${tenantId}/ping`;
  const conn = useConnections<PingConnection>(base);
  const [draft, setDraft] = useState<PingConnection | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setDraft(conn.selected ? { ...conn.selected } : null); setSaved(false); }, [conn.selected]);

  if (conn.items === null) return <p className="faint">Loading…</p>;
  if (conn.items.length === 0) {
    return <NoConnections what="PingAccess target" onCreate={conn.create} busy={conn.busy} />;
  }

  const set = <K extends keyof PingConnection>(key: K, value: PingConnection[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  const dirty = !!draft && !!conn.selected && JSON.stringify(draft) !== JSON.stringify(conn.selected);

  return (
    <>
      <ConnectionPicker
        items={conn.items} selectedId={conn.selectedId} onSelect={conn.setSelectedId}
        onCreate={conn.create} busy={conn.busy} newLabel="Target name"
      />

      {conn.error && <Banner kind="error">{conn.error}</Banner>}
      {saved && !dirty && <Banner kind="success">Saved.</Banner>}

      {draft && (
        <>
          <Card
            title="Put this URL behind PingAccess"
            hint="Create a PingAccess application whose target site is this server, then browse to the app URL through the PingAccess virtual host. The request that arrives here is analysed and reported."
          >
            <Copyable label="Protected application" value={draft.endpoints.appUrl} />
            <Copyable label="Raw header echo (JSON, nothing stored)" value={draft.endpoints.echoUrl} />
            <Copyable label="Health probe — exclude this path in PingAccess" value={draft.endpoints.healthUrl} />
            <div style={{ marginTop: 14 }}>
              <Code language="curl — simulate what PingAccess injects">
{`curl -s "${draft.endpoints.appUrl}" \\
  -H "accept: application/json" \\
  -H "${draft.tokenHeader}: <jwt>" \\
  -H "x-pa-user: jane.doe" | jq .`}
              </Code>
            </div>
          </Card>

          <Card
            title="Identity mapping"
            hint="Match this to the PingAccess identity mapping on the application."
          >
            <div className="grid two">
              <TextField
                label="Token header" value={draft.tokenHeader} onChange={(v) => set('tokenHeader', v)} mono
                placeholder="x-pa-token"
                hint="The header your JWT identity mapping writes into. Common: x-pa-token, x-access-token, authorization."
              />
              <SelectField
                label="Header value format" value={draft.tokenHeaderScheme}
                onChange={(v) => set('tokenHeaderScheme', v)}
                options={[
                  { value: 'none', label: 'raw token' },
                  { value: 'bearer', label: 'Bearer <token>' },
                ]}
              />
            </div>
            <ListField
              label="Identity header prefixes"
              value={draft.identityHeaderPrefixes} onChange={(v) => set('identityHeaderPrefixes', v)}
              placeholder={'x-pa-\nx-ping-'}
              hint="Headers starting with any of these are highlighted as injected identity attributes. One per line."
            />
            <ListField
              label="Required headers"
              value={draft.requiredHeaders} onChange={(v) => set('requiredHeaders', v)}
              placeholder="x-pa-user"
              hint="Each of these must arrive with a non-empty value or the test fails. One per line."
            />
          </Card>

          <Card
            title="Token validation"
            hint="Validates the mediated token the same way a real protected application should."
          >
            <CheckBox
              label="Validate the token signature" checked={draft.validateToken}
              onChange={(v) => set('validateToken', v)}
              hint="Turn off to inspect an opaque or deliberately broken token without failing the run."
            />
            <div className="grid two">
              <TextField
                label="JWKS URI" value={draft.jwksUri} onChange={(v) => set('jwksUri', v)} mono
                placeholder="https://pingfederate.example.com/pf/JWKS"
                hint="PingFederate publishes /pf/JWKS; a PingAccess JWT identity mapping publishes its own key set."
              />
              <TextField
                label="Expected issuer" value={draft.expectedIssuer} onChange={(v) => set('expectedIssuer', v)} mono
                placeholder="leave empty to skip the iss check"
              />
              <TextField
                label="Expected audience" value={draft.expectedAudience} onChange={(v) => set('expectedAudience', v)} mono
                placeholder="leave empty to skip the aud check"
              />
              <TextField
                label="Clock skew tolerance (s)" value={String(draft.clockSkewS)}
                onChange={(v) => set('clockSkewS', Number(v) || 0)} type="number"
              />
            </div>
            <ListField
              label="Required claims"
              value={draft.requiredClaims} onChange={(v) => set('requiredClaims', v)}
              placeholder={'sub\nemail'}
              hint="Each must be present and non-empty in the token. One per line."
            />
            <CheckBox label="Enabled" checked={draft.enabled} onChange={(v) => set('enabled', v)} />
          </Card>

          <div className="row" style={{ marginBottom: 30 }}>
            <button
              className="primary" disabled={!dirty || conn.busy}
              onClick={async () => { if (await conn.save(draft.id, draft)) setSaved(true); }}
            >{conn.busy ? 'Saving…' : 'Save changes'}</button>
            <button disabled={!dirty} onClick={() => setDraft({ ...conn.selected! })}>Discard</button>
            <div className="spacer" />
            <button
              className="danger"
              onClick={() => { if (confirm(`Delete PingAccess target "${draft.name}"?`)) void conn.remove(draft.id); }}
            >Delete target</button>
          </div>
        </>
      )}
    </>
  );
}
